<template>
    <div class="tag-input" :class="{ 'is-disabled': disabled }">
        <div class="tag-input-container" :class="{ 'is-invalid': error }" @click="focusInput">
            <span 
                v-for="(tag, index) in tags" 
                :key="index" 
                class="tag-item"
                :class="{ 'is-disabled': disabled }"
                @click.stop="editTag(index)"
            >
                {{ tag }}
                <button 
                    v-if="!disabled"
                    type="button" 
                    class="tag-remove" 
                    @click.stop="removeTag(index)"
                    aria-label="Remove tag"
                >
                    <img src="/img/icons/avid-icons/x-circle.svg" class="icon" alt="">
                </button>
            </span>
            <input
                v-if="!disabled"
                ref="input"
                type="text"
                v-model="inputValue"
                @keydown.enter.prevent="addTag"
                @keydown.comma.prevent="addTag"
                @keydown.delete="handleBackspace"
                @blur="addTag"
                :placeholder="tags.length === 0 ? placeholder : ''"
                class="tag-input-field" aria-label="Input Value">
        </div>
        <div v-if="error" class="invalid-feedback d-block">{{ error }}</div>
        <small v-if="helpText" class="form-text text-muted">{{ helpText }}</small>
    </div>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'TagInput',
    props: {
        modelValue: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Type and press Enter or comma to add...'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ''
        },
        helpText: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            inputValue: '',
            tags: []
        };
    },
    mounted() {
        this.parseModelValue();
    },
    watch: {
        modelValue(newValue) {
            this.parseModelValue();
        }
    },
    methods: {
        parseModelValue() {
            if (this.modelValue && this.modelValue.trim()) {
                // Split by comma, trim each, filter empty, and remove trailing commas
                const parsedTags = this.modelValue
                    .replace(/,\s*$/, '') // Remove trailing comma
                    .split(',')
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0);
                
                // Remove duplicates (case-insensitive) while preserving order
                const seen = new Set();
                this.tags = parsedTags.filter(tag => {
                    const tagLower = tag.toLowerCase();
                    if (seen.has(tagLower)) {
                        return false;
                    }
                    seen.add(tagLower);
                    return true;
                });
            } else {
                this.tags = [];
            }
        },
        addTag() {
            const tag = this.inputValue.trim();
            
            // Don't add empty tags
            if (!tag) {
                this.inputValue = '';
                return;
            }
            
            // Check for duplicates (case-insensitive)
            const tagLower = tag.toLowerCase();
            const isDuplicate = this.tags.some(existingTag => existingTag.toLowerCase() === tagLower);
            
            if (!isDuplicate) {
                this.tags.push(tag);
                this.inputValue = '';
                this.emitValue();
            } else {
                // Clear input even if duplicate (user feedback)
                this.inputValue = '';
            }
        },
        removeTag(index) {
            this.tags.splice(index, 1);
            this.emitValue();
        },
        editTag(index) {
            if (this.disabled) {
                return;
            }
            // Get the tag text
            const tagText = this.tags[index];
            // Remove the tag from the array
            this.tags.splice(index, 1);
            // Put the tag text into the input field
            this.inputValue = tagText;
            // Emit the updated value (without the tag)
            this.emitValue();
            // Focus the input field and select the text for easy editing
            this.$nextTick(() => {
                if (this.$refs.input) {
                    this.$refs.input.focus();
                    this.$refs.input.select();
                }
            });
        },
        handleBackspace() {
            if (this.inputValue === '' && this.tags.length > 0) {
                this.tags.pop();
                this.emitValue();
            }
        },
        emitValue() {
            // Join tags with comma and space, ensuring no trailing comma
            const value = this.tags.join(', ').replace(/,\s*$/, '');
            this.$emit('update:modelValue', value);
            this.$emit('change', value);
        },
        focusInput() {
            if (!this.disabled && this.$refs.input) {
                this.$refs.input.focus();
            }
        }
    }
});
</script>
